import { useEffect, useState } from "react";
import { Save, Loader2, Info } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const TOUCHPOINTS = ["TV", "OLV", "OTT", "Cinema"];

export default function DDF() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [matrix, setMatrix] = useState({}); // key: "TV|OLV" -> value

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/ddf");
    setEntries(data);
    const m = {};
    data.forEach((e) => { m[`${e.primary_touchpoint}|${e.secondary_touchpoint}`] = e.ddf_value; });
    setMatrix(m);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onChange = (primary, secondary, val) => {
    setMatrix({ ...matrix, [`${primary}|${secondary}`]: val });
  };

  const onSave = async () => {
    setSaving(true);
    try {
      for (let i = 0; i < TOUCHPOINTS.length; i++) {
        for (let j = i + 1; j < TOUCHPOINTS.length; j++) {
          const p = TOUCHPOINTS[i], s = TOUCHPOINTS[j];
          const v = matrix[`${p}|${s}`];
          if (v != null && v !== "") {
            await api.post("/ddf", {
              primary_touchpoint: p, secondary_touchpoint: s, ddf_value: Number(v),
            });
          }
        }
      }
      toast.success("DDF matrix saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  // Heatmap color
  const getColor = (val) => {
    if (val == null) return "bg-slate-50 text-slate-400";
    const intensity = Math.max(0, Math.min(1, (val - 1) * 10)); // ~1.0-1.1 mapping
    if (intensity < 0.2) return "bg-cyan-50 text-cyan-900";
    if (intensity < 0.4) return "bg-cyan-100 text-cyan-900";
    if (intensity < 0.6) return "bg-cyan-200 text-cyan-900";
    if (intensity < 0.8) return "bg-cyan-300 text-cyan-900";
    return "bg-[#0A3D91] text-white";
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold text-slate-900">De-Duplication Matrix</h2>
          <p className="text-sm text-slate-500 mt-1">
            DDF values represent overlap between touchpoints. Enter as decimal (1.061) or percent (106.1).
          </p>
        </div>
        <button onClick={onSave} disabled={saving} className="btn-primary" data-testid="ddf-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save Matrix
        </button>
      </div>

      <div className="card-harmony p-5">
        <div className="flex items-center gap-2 text-xs text-slate-500 mb-4 p-3 bg-slate-50 rounded-lg">
          <Info className="h-4 w-4 text-[#0E73C9]" />
          Combined Reach = TV + OLV − (DDF × TV × OLV). Higher DDF = more overlap. Typical TV×OLV ≈ 1.061.
        </div>

        {loading ? (
          <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="ddf-matrix">
              <thead>
                <tr>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500"></th>
                  {TOUCHPOINTS.map((t) => (
                    <th key={t} className="text-center py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">{t}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {TOUCHPOINTS.map((p, i) => (
                  <tr key={p}>
                    <td className="py-3 px-4 text-sm font-semibold text-slate-700 uppercase tracking-wider">{p}</td>
                    {TOUCHPOINTS.map((s, j) => {
                      if (i === j) return <td key={s} className="p-2"><div className="aspect-square bg-slate-100 rounded-lg flex items-center justify-center text-slate-400 text-sm">—</div></td>;
                      if (i > j) return <td key={s} className="p-2"><div className="aspect-square bg-slate-50 rounded-lg flex items-center justify-center text-slate-300 text-xs">·</div></td>;
                      const key = `${p}|${s}`;
                      const val = matrix[key];
                      return (
                        <td key={s} className="p-2">
                          <div className={`aspect-square rounded-lg flex flex-col items-center justify-center border-2 border-transparent hover:border-[#0E73C9] transition-all ${getColor(val)}`}>
                            <input
                              type="number"
                              step="0.001"
                              value={val ?? ""}
                              onChange={(e) => onChange(p, s, e.target.value)}
                              data-testid={`ddf-${p}-${s}`}
                              className="w-full bg-transparent text-center font-bold text-base outline-none"
                            />
                            <div className="text-[9px] uppercase tracking-wider mt-0.5 opacity-70">{p}×{s}</div>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
